/* FLUSSO · MODIFICA DAL VIVO
 *
 * Si modifica un bot mentre opera, spesso con una posizione aperta e con soldi
 * veri dentro. Perciò:
 *
 *  - ogni parametro si legge come *da → a*, col vecchio valore barrato: si deve
 *    vedere cosa si sta sostituendo, non solo cosa si sta scrivendo;
 *  - le modifiche pendenti si contano, perché "applica" senza sapere quante
 *    cose cambia è un salto nel buio;
 *  - il controllo che il vecchio flusso non aveva: se una soglia è GIÀ superata
 *    dalla posizione aperta — take profit sotto il guadagno attuale, stop loss
 *    sopra la perdita attuale — quella modifica non protegge la posizione, la
 *    CHIUDE alla prossima candela. Va detto, e il pulsante diventa "Applica
 *    comunque": la modifica resta possibile, ma non può passare per una svista.
 */

import { useEffect, useMemo, useState } from 'react';
import { Avvertenza, Campo, Modale, Occhiello, Pulsante, Scheda } from '../../componenti';
import { tradebots } from '../../api';
import type { Tradebot } from '../../api';
import { num, ora, pct } from '../../lib/formato';
import { tonoRendimento } from '../../lib/metriche';
import {
  allarmeSoglia,
  classificaSoglia,
  modalitaBot,
  parametriModificabili,
  parametriNonModificabili,
  posizione,
  statoBot,
  type AllarmeSoglia,
  type ParametroModificabile,
  type ValoreParametro,
} from './modello';
import { DistintiviModalita, IconaModifica, Interruttore } from './Comuni';

/** Valore in lavorazione: i numeri restano stringhe finché si digita, altrimenti
 *  cancellare l'ultima cifra farebbe saltare il campo a zero. */
type Bozza = Record<string, string | boolean>;

export function FoglioModifica({
  bot,
  aperta,
  onChiudi,
  onApplicato,
}: {
  bot: Tradebot | null;
  aperta: boolean;
  onChiudi: () => void;
  onApplicato: (quante: number) => void;
}) {
  const [bozza, setBozza] = useState<Bozza>({});
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const parametri = useMemo(() => (bot ? parametriModificabili(bot) : []), [bot]);
  const nonModificabili = useMemo(() => (bot ? parametriNonModificabili(bot) : []), [bot]);

  const idBot = bot?.id ?? 0;

  /* Ogni apertura riparte dai valori veri del bot: una bozza sopravvissuta
     all'apertura precedente scriverebbe su un bot i parametri di un altro. */
  useEffect(() => {
    if (!aperta) return;
    const iniziale: Bozza = {};
    for (const p of parametri) {
      iniziale[p.chiave] = typeof p.valore === 'boolean' ? p.valore : String(p.valore);
    }
    setBozza(iniziale);
    setErrore(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aperta, idBot]);

  const pos = bot ? posizione(bot) : null;

  const cambiati = useMemo(
    () => parametri.filter((p) => eCambiato(p, bozza[p.chiave])),
    [parametri, bozza],
  );

  /* Gli allarmi si calcolano sul valore che si sta per SCRIVERE, non su quello
     salvato: è quello che deciderà la prossima candela. */
  const allarmi = useMemo(() => {
    if (!pos || !pos.aperta) return [] as { allarme: AllarmeSoglia; cambiato: boolean }[];
    const fuori: { allarme: AllarmeSoglia; cambiato: boolean }[] = [];
    for (const p of parametri) {
      if (p.tipo !== 'numero' || !classificaSoglia(p.chiave)) continue;
      const grezzo = bozza[p.chiave];
      const valore = Number(typeof grezzo === 'string' ? grezzo.replace(',', '.') : p.valore);
      if (!Number.isFinite(valore)) continue;
      const allarme = allarmeSoglia(p.chiave, valore, pos);
      if (allarme) fuori.push({ allarme, cambiato: eCambiato(p, grezzo) });
    }
    return fuori;
  }, [parametri, bozza, pos]);

  const allarmiSuModifiche = allarmi.filter((a) => a.cambiato);
  const invalidi = cambiati.filter((p) => !valoreValido(p, bozza[p.chiave]));

  const quante = cambiati.length;
  const puoApplicare = quante > 0 && invalidi.length === 0 && !inCorso;

  const etichettaPulsante = inCorso
    ? 'Applico…'
    : quante === 0
      ? 'Nessuna modifica'
      : invalidi.length > 0
        ? 'Valori da correggere'
        : allarmiSuModifiche.length > 0
          ? 'Applica comunque'
          : quante === 1
            ? 'Applica 1 modifica'
            : `Applica ${quante} modifiche`;

  async function applica() {
    if (!bot || !puoApplicare) return;
    setInCorso(true);
    setErrore(null);

    /* `strategyParams` è una colonna JSON: la PATCH la sostituisce intera,
       quindi va rimandata completa. Inviare solo le chiavi toccate
       cancellerebbe tutte le altre. */
    const corpo: Record<string, unknown> = {};
    const strategia: Record<string, unknown> = { ...(bot.strategyParams ?? {}) };
    let strategiaToccata = false;

    for (const p of cambiati) {
      const valore = valoreFinale(p, bozza[p.chiave]);
      if (p.dentroStrategia) {
        strategia[p.chiave.replace(/^strategyParams\./, '')] = valore;
        strategiaToccata = true;
      } else {
        corpo[p.chiave] = valore;
      }
    }
    if (strategiaToccata) corpo.strategyParams = strategia;

    try {
      await tradebots.aggiorna(bot.id, corpo as Partial<Tradebot>);
      setInCorso(false);
      onApplicato(quante);
    } catch (e) {
      setInCorso(false);
      setErrore(e instanceof Error ? e.message : 'Modifica non riuscita.');
    }
  }

  if (!bot) return null;

  const stato = statoBot(bot);
  const modalita = modalitaBot(bot);

  return (
    <Modale
      aperta={aperta}
      onChiudi={onChiudi}
      titolo="Modifica dal vivo"
      sottotitolo={`${bot.asset}/${bot.currency} · ${bot.strategyName} · #${bot.id}`}
      pieda={
        <>
          <Pulsante onClick={onChiudi} disabled={inCorso}>
            Annulla
          </Pulsante>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Pulsante
              variante={allarmiSuModifiche.length > 0 ? 'cautela' : 'primario'}
              largo
              onClick={applica}
              disabled={!puoApplicare}
            >
              <IconaModifica />
              {etichettaPulsante}
            </Pulsante>
            {quante > 0 && (
              <span className={`tb-conteggio${quante === 0 ? ' tb-conteggio--nessuna' : ''}`}>
                {quante === 1 ? '1 modifica pendente' : `${quante} modifiche pendenti`}
              </span>
            )}
          </div>
        </>
      }
    >
      {/* ---------- dove sta il bot adesso ---------- */}
      <Scheda variante="incassata">
        <div className="tb-testata__titolo">
          <span className={`tb-stato-etichetta sb-tono-${stato.tono}`}>{stato.etichetta}</span>
          <DistintiviModalita bot={bot} />
        </div>
        {pos?.aperta ? (
          <div className="tb-sotto">
            posizione aperta a {pos.prezzoIngresso != null ? num(pos.prezzoIngresso, 6) : '—'}{' '}
            {bot.currency}, ultimo prezzo scritto dal bot{' '}
            {pos.prezzoScritto != null ? num(pos.prezzoScritto, 6) : '—'} ·{' '}
            {/* Il tono passa da `tonoRendimento`, che sul nullo restituisce
                `neutro`. Prima il colore veniva da `(… ?? 0) >= 0`, cioè un
                `n/d` si presentava in VERDE: non inventava una cifra,
                inventava un giudizio — ed è il giudizio che si legge per
                primo. */}
            <span className={`sb-tono-${tonoRendimento(pos.variazionePct)}`}>
              {pos.variazionePct != null ? pct(pos.variazionePct, 2) : 'n/d'}
            </span>{' '}
            · letto dal bot alle {ora(bot.updatedAt)}
          </div>
        ) : (
          <div className="tb-sotto">
            fuori mercato: nessuna posizione aperta, le soglie di uscita non hanno effetto adesso
          </div>
        )}
      </Scheda>

      {modalita.denaroReale && (
        <Avvertenza livello="cautela">
          Questo bot opera con <b>denaro vero</b>. Le modifiche valgono sul capitale reale, non su
          una simulazione.
        </Avvertenza>
      )}

      {/* ---------- i parametri, da → a ---------- */}
      {parametri.length === 0 ? (
        <Avvertenza livello="nota">
          Questo bot non espone parametri scalari modificabili: <code>strategyParams</code> è vuoto
          e non ci sono campi numerici da cambiare a caldo.
        </Avvertenza>
      ) : (
        parametri.map((p) => (
          <RigaParametro
            key={p.chiave}
            parametro={p}
            bozza={bozza[p.chiave]}
            allarme={allarmi.find((a) => a.allarme.tipo === classificaSoglia(p.chiave))?.allarme}
            onCambia={(v) => setBozza((b) => ({ ...b, [p.chiave]: v }))}
          />
        ))
      )}

      {nonModificabili.length > 0 && (
        <div>
          <Occhiello>Parametri non modificabili da qui</Occhiello>
          <div className="tb-voci" style={{ marginTop: 'var(--sb-s-4)' }}>
            {nonModificabili.map((p) => (
              <div className="tb-voce" key={p.chiave}>
                <span className="tb-voce__chiave">{p.chiave}</span>
                <span className="tb-voce__valore">{p.valore}</span>
              </div>
            ))}
          </div>
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
            Non sono numeri né interruttori: si mostrano perché nasconderli li farebbe cercare
            altrove, ma vanno cambiati ricreando il bot.
          </div>
        </div>
      )}

      {/* ---------- gli allarmi di soglia ---------- */}
      {allarmi.map(({ allarme, cambiato }) => (
        <Avvertenza key={allarme.tipo} livello={cambiato ? 'grave' : 'cautela'}>
          <b>{allarme.etichetta}.</b> {allarme.testo}
          {!cambiato && ' Vale già col valore salvato: applicando altre modifiche non cambia.'}
        </Avvertenza>
      ))}

      {invalidi.length > 0 && (
        <Avvertenza livello="grave">
          {invalidi.length === 1
            ? `Il valore di “${invalidi[0].etichetta}” non è un numero valido.`
            : `${invalidi.length} valori non sono numeri validi.`}{' '}
          Correggili prima di applicare.
        </Avvertenza>
      )}

      {errore && <Avvertenza livello="grave">{errore}</Avvertenza>}

      <Avvertenza livello="nota">
        Le modifiche valgono <b>dalla prossima candela</b> ({bot.candleSize} minuti). La posizione
        aperta non viene chiusa da questo salvataggio e gli ordini già a mercato non cambiano: è il
        processo del bot a rileggere i parametri quando ci arriva.
      </Avvertenza>
    </Modale>
  );
}

/* ==========================================================================
 * Una riga di parametro: da → a, col vecchio barrato
 * ======================================================================== */

function RigaParametro({
  parametro: p,
  bozza,
  allarme,
  onCambia,
}: {
  parametro: ParametroModificabile;
  bozza: string | boolean | undefined;
  allarme?: AllarmeSoglia;
  onCambia: (v: string | boolean) => void;
}) {
  const cambiato = eCambiato(p, bozza);
  const valido = valoreValido(p, bozza);

  const da = testoValore(p.valore, p.unita);
  const a = bozza == null ? da : testoValore(bozza, p.unita);

  return (
    /* Il nome del parametro compare una volta sola, qui: il campo sotto si
       chiama "Nuovo valore" e prende il contesto dal gruppo, così la riga
       "da → a" e l'etichetta del campo non si ripetono a vicenda. */
    <div className="tb-parametro" role="group" aria-label={p.etichetta}>
      <div className="tb-parametro__testata">
        <span className="tb-parametro__nome">
          {p.etichetta}
          {classificaSoglia(p.chiave) && (
            <span className="sb-nota" style={{ display: 'block' }}>
              soglia di uscita: decide quando la posizione si chiude
            </span>
          )}
        </span>
        <span className="tb-transizione">
          <span className={`tb-transizione__da${cambiato ? ' tb-transizione__da--sostituito' : ''}`}>
            {da}
          </span>
          <span className="tb-transizione__freccia" aria-hidden="true">
            →
          </span>
          <span className={`tb-transizione__a${cambiato ? ' tb-transizione__a--cambiato' : ''}`}>
            {a}
          </span>
        </span>
      </div>

      {p.tipo === 'booleano' ? (
        <Interruttore
          etichetta={bozza === true ? 'Acceso' : 'Spento'}
          acceso={bozza === true}
          onCambia={() => onCambia(!(bozza === true))}
        />
      ) : (
        <Campo
          etichetta="Nuovo valore"
          type={p.tipo === 'numero' ? 'number' : 'text'}
          inputMode={p.tipo === 'numero' ? 'decimal' : undefined}
          step="any"
          testuale={p.tipo === 'testo'}
          unita={p.unita}
          value={typeof bozza === 'string' ? bozza : ''}
          onChange={(e) => onCambia(e.target.value)}
          errore={!valido ? 'Serve un numero.' : allarme && cambiato ? allarme.etichetta : null}
        />
      )}
    </div>
  );
}

/* ==========================================================================
 * Confronto e conversione
 * ======================================================================== */

function eCambiato(p: ParametroModificabile, bozza: string | boolean | undefined): boolean {
  if (bozza === undefined) return false;
  if (p.tipo === 'booleano') return bozza !== p.valore;
  if (typeof bozza !== 'string') return false;
  if (p.tipo === 'testo') return bozza !== String(p.valore);
  /* Confronto numerico e non testuale: "5" e "5.0" sono lo stesso valore, e
     contarli come modifica farebbe apparire modifiche pendenti inesistenti. */
  const n = Number(bozza.replace(',', '.'));
  if (!Number.isFinite(n)) return true;
  return n !== Number(p.valore);
}

function valoreValido(p: ParametroModificabile, bozza: string | boolean | undefined): boolean {
  if (p.tipo !== 'numero') return true;
  if (typeof bozza !== 'string') return false;
  return bozza.trim() !== '' && Number.isFinite(Number(bozza.replace(',', '.')));
}

function valoreFinale(p: ParametroModificabile, bozza: string | boolean | undefined): ValoreParametro {
  if (p.tipo === 'booleano') return bozza === true;
  if (typeof bozza !== 'string') return p.valore;
  if (p.tipo === 'numero') return Number(bozza.replace(',', '.'));
  return bozza;
}

function testoValore(v: string | boolean | number, unita?: string): string {
  if (typeof v === 'boolean') return v ? 'acceso' : 'spento';
  if (typeof v === 'number') return `${num(v, decimaliDi(v))}${unita ?? ''}`;
  if (v.trim() === '') return '—';
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) ? `${num(n, decimaliDi(n))}${unita ?? ''}` : v;
}

/** Un intero resta intero, un decimale non perde le cifre che ha. */
function decimaliDi(n: number): number {
  if (Number.isInteger(n)) return 0;
  const testo = String(n);
  const punto = testo.indexOf('.');
  return punto < 0 ? 0 : Math.min(6, testo.length - punto - 1);
}
